import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { debounceTime, filter } from 'rxjs';
import { ConnectService } from '../connect/connect.service';
import {
  ScandataDisplayStatus,
  ScandataLoadStatus,
  ScandataModel,
} from '../scandata/scandata.models';
import { ScandataState } from '../scandata/scandata.state';
import { StationLoadStatus } from '../station/station.models';

export const HOST_3D_FIT_TO_VIEW_REARM_TIME = 1000;

@Injectable({
  providedIn: 'root',
})
export class Host3dFitToViewService {
  private readonly connectService = inject(ConnectService);
  private readonly store = inject(Store);

  private canFit = true;

  constructor() {
    this.subscribeToFitWhenSettled();
  }

  fitToView(scans: ScandataModel[] = []) {
    if (!this.canFit) return;

    const currentScans = this.store.selectSnapshot(ScandataState.scandata);
    const web3dId = this.getFirstDisplayedWeb3dId(scans, currentScans);

    if (!isDefined(web3dId)) return;

    this.canFit = false;

    void this.connectService.fitToView([web3dId]).catch((error) => {
      console.error('FitToView failed:', error);
    });
  }

  private subscribeToFitWhenSettled() {
    this.store
      .select(ScandataState.scandata)
      .pipe(
        debounceTime(HOST_3D_FIT_TO_VIEW_REARM_TIME),
        filter((scans) => !this.canFit && !this.hasPendingScans(scans)),
      )
      .subscribe(() => (this.canFit = true));
  }

  private getFirstDisplayedWeb3dId(
    scans: ScandataModel[],
    currentScans: ScandataModel[],
  ): string | undefined {
    for (const scan of scans) {
      const current = currentScans.find((s) => s.id === scan.id);

      if (
        current?.displayStatus === ScandataDisplayStatus.Displayed &&
        isDefined(current.web3dId)
      ) {
        return current.web3dId;
      }
    }

    return undefined;
  }

  // Only in-flight states that resolve on their own. Resting states like
  // Loaded + Hidden are excluded on purpose: a scan can be stranded there and
  // would pin the gate closed forever, permanently breaking fit-to-view.
  private hasPendingScans(scans: ScandataModel[]): boolean {
    return scans.some(
      (scan) =>
        scan.loadStatus === ScandataLoadStatus.Loading ||
        scan.stationLoadStatus === StationLoadStatus.Loading ||
        scan.displayStatus === ScandataDisplayStatus.AwaitingDisplay,
    );
  }
}
